import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store';
import { logout } from '../../../Login/authslice';
import './Sidebar.css';
import AppLogo from '../../../Images/SLB_Logo_White_General.png';
import HomeIcon from '../../../Images/home-line.png';
import BellIcon from '../../../Images/bell_icon.png';
import SettingsIcon from '../../../Images/settings-icon.svg';
import LogoutIcon from '../../../Images/logout.svg';
import UserIcon from '../../../Images/user-icon.svg';
import UserPreferences from '../../UserPreferences';
import { ENV_CONFIG } from '../../../config/envConfig';
import { decodeToken } from '../../../Services/Users.service';

const Sidebar: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userRole = useSelector((state: RootState) => state.auth.userRole);
    const userEmail = useSelector((state: RootState) => state.auth.userId); // This contains the email
    const [showUserPreferences, setShowUserPreferences] = useState(false);
  
    // Extract username from email for display (same as UserPreferences component)
    const getUsernameFromEmail = (email: string | null): string => {
        if (!email) return 'User';
        const username = email.split('@')[0];
        // Capitalize first letter and replace dots/underscores with spaces
        return username
            .replace(/[._]/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };
    
   

    // Extract full user info from token (same as UserPreferences component)
    const extractUserInfoFromToken = () => {
        const accessToken = localStorage.getItem('access_token');
        if (accessToken) {
            try {
                const decoded = decodeToken(accessToken) as any;
                
                const userInfo = {
                    email: decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
                    name: decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
                    role: decoded?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
                    userType: decoded?.UserType,
                    userId: decoded?.UserId
                };
                
                return userInfo;
            } catch (error) {
                return null;
            }
        }
        return null;
    };
    
    const tokenUserInfo = extractUserInfoFromToken();
    const rawRole = userRole || tokenUserInfo?.role || localStorage.getItem('userRole');
    const normalizedRole = rawRole ? rawRole.toLowerCase().replace(/\s+/g, '') : '';

    // Try to get the display username from various sources (same as UserPreferences component)
    const getDisplayUsername = (): string => {
        // Try multiple sources for email/username
        const emailToUse = userEmail || 
                          tokenUserInfo?.email ||
                          localStorage.getItem('loginemail') || 
                          localStorage.getItem('userId');
        
        // Use name from token if available
        if (tokenUserInfo?.name) {
            return tokenUserInfo.name;
        }
        
        return getUsernameFromEmail(emailToUse);
    };

    const displayUsername = getDisplayUsername();

    const handleLogout = () => {
        dispatch(logout());
        localStorage.clear();
        // If you have a central logout URL (SSO), use it:
        if (ENV_CONFIG.logoutUrl) {
            window.location.assign(ENV_CONFIG.logoutUrl);
        } else {
            navigate('/login');
        }
    };

    useEffect(() => {
    }, [userRole]);

    return (
        <div className="sidebar-container">
            {/* Top Logo */}
            <div className="sidebar-logo">
                <img src={AppLogo} alt="SLB Logo" className="app-logo" />
            </div>

            {/* Main Navigation Icons */}
            <nav className="sidebar-nav">
                <ul>
                    <li>
                        <NavLink
                            to="/home"
                            className={({ isActive }) => (isActive ? "nav-icon-link active" : "nav-icon-link")}
                            title="Home"
                                 onClick={() => {
                        // Scroll to top when navigating to home
                        window.scrollTo(0, 0);
                    }}
                        >
                            <img src={HomeIcon} alt="Home" />
                        </NavLink>
                    </li>
                    <li>
                        <NavLink
                            to="/alerts"
                            className={({ isActive }) => (isActive ? "nav-icon-link active" : "nav-icon-link")}
                            title="Alerts"
                                 onClick={() => {
                        // Scroll to top when navigating to alerts
                        window.scrollTo(0, 0);
                    }}
                        >
                            <img src={BellIcon} alt="Notifications" />
                        </NavLink>
                    </li>
                </ul>
            </nav>

            {/* Bottom Section Icons */}
            <nav className="sidebar-bottom-nav">
                <ul>
                     {(normalizedRole === "admin" || normalizedRole === "globaladmin") && ( 
                        <li>
                            <NavLink
                                to="/plants"
                                className={({ isActive }) => (isActive ? "nav-icon-link active" : "nav-icon-link")}
                                title="Settings"
                                onClick={() => {
                        // Scroll to top when navigating to settings
                        window.scrollTo(0, 0);
                    }}
                            >
                                <img src={SettingsIcon} alt="Settings" />
                            </NavLink>
                        </li>
                     )} 
                    <li>
                        <button
                            type="button"
                            className="nav-icon-link user-preferences-icon"
                            onClick={() => setShowUserPreferences(true)}
                            title={displayUsername}
                            aria-label={`Open user preferences for ${displayUsername}`}
                        >
                            <img src={UserIcon} alt="" />
                        </button>
                    </li>
                    {/* <li>
                        <div className='nav-icon-link'><img src={HelpIcon} alt="Help" /></div>
                    </li> */}
                    <li>
                        <button
                            type="button"
                            className="nav-icon-link"
                            onClick={handleLogout}
                            title="Logout"
                            aria-label="Logout"
                        >
                            <img src={LogoutIcon} alt="" />
                        </button>
                    </li>
                </ul>
            </nav>

            {/* User Preferences Modal */}
            {showUserPreferences && (
                <UserPreferences onClose={() => setShowUserPreferences(false)} />
            )}
        </div>
    );
};

export default Sidebar;